import { useState, useEffect, useRef } from 'react';
import { watchResource, listResource } from '../services/k8s-proxy-client';

/**
 * Custom hook for watching Kubernetes resources in real-time
 * 
 * @param resourceType The type of resource to watch (pods, nodes, etc.)
 * @param namespace The namespace to filter by (empty for cluster-scoped resources)
 * @returns Object containing resources, loading state, and error
 */
export function useWatchResource(resourceType: string, namespace = '') {
  const [resources, setResources] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const resourceCache = useRef(new Map<string, any>());
  const resourceVersionRef = useRef('');

  useEffect(() => {
    const controller = new AbortController();
    let retryTimeout: NodeJS.Timeout | undefined;

    async function startWatching() {
      try {
        setIsLoading(true);
        
        // Initial load of resources
        const initialData = await listResource(resourceType, namespace);
        
        // Update resource version
        resourceVersionRef.current = initialData.metadata.resourceVersion;
        
        // Initialize cache with current resources
        resourceCache.current.clear();
        initialData.items.forEach((item: any) => {
          const key = `${item.metadata.namespace || ''}-${item.metadata.name}`;
          resourceCache.current.set(key, item);
        });
        
        // Update state with initial resources
        setResources(Array.from(resourceCache.current.values()));
        setIsLoading(false);
        
        // Start watching for changes
        watchResourceWithRetry();
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
        setIsLoading(false);
        // Retry after 5 seconds on error
        retryTimeout = setTimeout(startWatching, 5000);
      }
    }

    async function watchResourceWithRetry() {
      try {
        await watchResource(
          resourceType, 
          namespace,
          resourceVersionRef.current,
          handleResourceEvent,
          controller.signal
        );
        
        // If watch ends normally, restart it
        watchResourceWithRetry();
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err : new Error('Unknown error occurred'));
          // Retry after delay
          retryTimeout = setTimeout(watchResourceWithRetry, 5000);
        }
      }
    }

    function handleResourceEvent(event: any) {
      const resource = event.object;
      const key = `${resource.metadata.namespace || ''}-${resource.metadata.name}`;
      
      // Update resource version
      resourceVersionRef.current = resource.metadata.resourceVersion;
      
      switch (event.type) {
        case 'ADDED':
        case 'MODIFIED':
          resourceCache.current.set(key, resource);
          break;
        case 'DELETED':
          resourceCache.current.delete(key);
          break;
        default:
          console.warn(`Unknown event type: ${event.type}`);
      }
      
      // Update state with current resources
      setResources(Array.from(resourceCache.current.values()));
    }

    startWatching();

    return () => {
      controller.abort();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [resourceType, namespace]);

  return { resources, isLoading, error };
} 